export const PosAction = {
  Cost: "cost",
  Discount: "discount",
  VoidTransaction: "void_transaction",
  PriceAdjustment: "price_adjustment",
  PrintReceipt: "print_receipt",
  SendToKitchen: "send_to_kitchen",
  ShowProfitMargin: "show_profit_margin",
  HoldOrder: "hold_order",
  ExchangeRate: "exchange_rate",
  DeleteBill: "delete_bill",
  ReprintBill: "reprint_bill",
  QuickSale: "quick_sale",
} as const;

export type PosPolicy = (typeof PosAction)[keyof typeof PosAction];

export const SalesOrderAction = {
  Edit: "edit",
  Delete: "delete",
  Refund: "refund",
  ViewProfit: "view_profit",
  View: "view",
  Export: "export",
  PrintInvoice: "print_invoice",
} as const;

export type SalesOrderPolicy = (typeof SalesOrderAction)[keyof typeof SalesOrderAction];

export const SalesReturnAction = {
  View: "view",
  Create: "create",
  Approve: "approve",
} as const;

export type SalesReturnPolicy = (typeof SalesReturnAction)[keyof typeof SalesReturnAction];

export const ExpenseAction = {
  View: "view",
  Create: "create",
  Edit: "edit",
  Delete: "delete",
} as const;

export type ExpensesPolicy = (typeof ExpenseAction)[keyof typeof ExpenseAction];

export const ChequeAction = {
  View: "view",
  Create: "create",
  Deposit: "deposit",
  Bounce: "bounce",
  Delete: "delete",
} as const;

export type ChequesPolicy = (typeof ChequeAction)[keyof typeof ChequeAction];

export const QuotationAction = {
  View: "view",
  Create: "create",
  Edit: "edit",
  Delete: "delete",
  ConvertToSale: "convert_to_sale",
  Print: "print",
} as const;

export type QuotationPolicy = (typeof QuotationAction)[keyof typeof QuotationAction];

export const RepairJobAction = {
  View: "view",
  Create: "create",
  Edit: "edit",
  Delete: "delete",
  UpdateStatus: "update_status",
} as const;

export type RepairJobsPolicy = (typeof RepairJobAction)[keyof typeof RepairJobAction];

export const ProductAction = {
  Create: "create",
  Edit: "edit",
  Delete: "delete",
  EditCost: "edit_cost",
  EditPrice: "edit_price",
  ViewCost: "view_cost",
  Export: "export",
  Import: "import",
  BulkPriceUpdate: "bulk_price_update",
  Transfer: "transfer",
  StockAdjustment: "stock_adjustment",
  ViewStock: "view_stock",
  ManageCategories: "manage_categories",
} as const;

export type ProductsPolicy = (typeof ProductAction)[keyof typeof ProductAction];

export const CustomerAction = {
  Delete: "delete",
  ViewCreditLimit: "view_credit_limit",
  AdjustCredit: "adjust_credit",
  Export: "export",
  Create: "create",
  Edit: "edit",
  ViewDetails: "view_details",
} as const;

export type CustomersPolicy = (typeof CustomerAction)[keyof typeof CustomerAction];

export const SupplierAction = {
  View: "view",
  Create: "create",
  Edit: "edit",
  Delete: "delete",
} as const;

export type SuppliersPolicy = (typeof SupplierAction)[keyof typeof SupplierAction];

export const CreditAction = {
  View: "view",
  CreatePayment: "create_payment",
  Adjust: "adjust",
} as const;

export type CreditsPolicy = (typeof CreditAction)[keyof typeof CreditAction];

export const PurchaseAction = {
  Create: "create",
  Edit: "edit",
  Delete: "delete",
  Approve: "approve",
  Receive: "receive",
  Pay: "pay",
  View: "view",
} as const;

export type PurchasesPolicy = (typeof PurchaseAction)[keyof typeof PurchaseAction];

export const ReportAction = {
  ViewProfit: "view_profit",
  ViewCost: "view_cost",
  ViewSalary: "view_salary",
  ViewAttendance: "view_attendance",
  Export: "export",
  ViewRevenue: "view_revenue",
  ViewPurchase: "view_purchase",
  ViewExpense: "view_expense",
  ViewCashflow: "view_cashflow",
  ViewDiscount: "view_discount",
  ViewEmployeePerformance: "view_employee_performance",
} as const;

export type ReportsPolicy = (typeof ReportAction)[keyof typeof ReportAction];

export const AdminAction = {
  ManageUsers: "manage_users",
  ManageRoles: "manage_roles",
  ManageStores: "manage_stores",
  ViewLogs: "view_logs",
  ManageBarcodeTemplates: "manage_barcode_templates",
  ManagePrinters: "manage_printers",
  ManageBanks: "manage_banks",
  ManageStoreTracking: "manage_store_tracking",
} as const;

export type AdminPolicy = (typeof AdminAction)[keyof typeof AdminAction];

export const HRAction = {
  ManageEmployees: "manage_employees",
  ManageAttendance: "manage_attendance",
  ManagePayroll: "manage_payroll",
  ManageLeaves: "manage_leaves",
  ViewMonthlyAttendance: "view_monthly_attendance",
} as const;

export type HRPolicy = (typeof HRAction)[keyof typeof HRAction];

export const RawMaterialAction = {
  Manage: "manage",
  Purchase: "purchase",
  ManageRecipes: "manage_recipes",
  ManageProduction: "manage_production",
  View: "view",
} as const;

export type RawMaterialsPolicy = (typeof RawMaterialAction)[keyof typeof RawMaterialAction];

export const RawPurchaseAction = {
  View: "view",
  Create: "create",
  Edit: "edit",
  Delete: "delete",
  Approve: "approve",
} as const;

export type RawPurchasesPolicy = (typeof RawPurchaseAction)[keyof typeof RawPurchaseAction];

export const DashboardAction = {
  ViewOverview: "view_overview",
  ViewFinancialSummary: "view_financial_summary",
  ViewTopSelling: "view_top_selling",
  ViewLowStock: "view_low_stock",
  ViewAnalytics: "view_analytics",
} as const;

export type DashboardPolicy = (typeof DashboardAction)[keyof typeof DashboardAction];

export const IntegrationAction = {
  WhatsAppChatbot: "whatsapp_chatbot",
  OnlineOrders: "online_orders",
} as const;

export type IntegrationsPolicy = (typeof IntegrationAction)[keyof typeof IntegrationAction];

export type PolicyPath =
  | `pos.${PosPolicy}`
  | `sales_order.${SalesOrderPolicy}`
  | `sales_return.${SalesReturnPolicy}`
  | `expenses.${ExpensesPolicy}`
  | `cheques.${ChequesPolicy}`
  | `quotation.${QuotationPolicy}`
  | `repair_jobs.${RepairJobsPolicy}`
  | `products.${ProductsPolicy}`
  | `customers.${CustomersPolicy}`
  | `suppliers.${SuppliersPolicy}`
  | `credits.${CreditsPolicy}`
  | `purchases.${PurchasesPolicy}`
  | `reports.${ReportsPolicy}`
  | `admin.${AdminPolicy}`
  | `hr.${HRPolicy}`
  | `raw_materials.${RawMaterialsPolicy}`
  | `raw_purchases.${RawPurchasesPolicy}`
  | `dashboard.${DashboardPolicy}`
  | `integrations.${IntegrationsPolicy}`;

export type SectionPolicy<S extends string> =
  S extends "pos" ? PosPolicy :
  S extends "sales_order" ? SalesOrderPolicy :
  S extends "sales_return" ? SalesReturnPolicy :
  S extends "expenses" ? ExpensesPolicy :
  S extends "cheques" ? ChequesPolicy :
  S extends "quotation" ? QuotationPolicy :
  S extends "repair_jobs" ? RepairJobsPolicy :
  S extends "products" ? ProductsPolicy :
  S extends "customers" ? CustomersPolicy :
  S extends "suppliers" ? SuppliersPolicy :
  S extends "credits" ? CreditsPolicy :
  S extends "purchases" ? PurchasesPolicy :
  S extends "reports" ? ReportsPolicy :
  S extends "admin" ? AdminPolicy :
  S extends "hr" ? HRPolicy :
  S extends "raw_materials" ? RawMaterialsPolicy :
  S extends "raw_purchases" ? RawPurchasesPolicy :
  S extends "dashboard" ? DashboardPolicy :
  S extends "integrations" ? IntegrationsPolicy :
  never;

export type SectionName =
  | "pos" | "sales_order" | "sales_return" | "expenses" | "cheques"
  | "quotation" | "repair_jobs" | "products" | "customers" | "suppliers"
  | "credits" | "purchases" | "reports" | "admin" | "hr"
  | "raw_materials" | "raw_purchases" | "dashboard"
  | "integrations";

export type AllPolicies =
  | PosPolicy | SalesOrderPolicy | SalesReturnPolicy
  | ExpensesPolicy | ChequesPolicy | QuotationPolicy
  | RepairJobsPolicy | ProductsPolicy | CustomersPolicy
  | SuppliersPolicy | CreditsPolicy | PurchasesPolicy
  | ReportsPolicy | AdminPolicy | HRPolicy
  | RawMaterialsPolicy | RawPurchasesPolicy | DashboardPolicy
  | IntegrationsPolicy;

export type AccessLevel = "granted" | "denied" | "conditional";

export interface AccessCheckResult {
  allowed: boolean;
  level: AccessLevel;
  reason?: string;
  policyPath?: string;
}

export interface AccessibleFeature {
  code: string;
  name: string;
  featureCode: string;
  isEnabled: boolean;
  isDisabled: boolean;
}

export interface RBAProfile {
  role: string;
  isSuperAdmin: boolean;
  accessibleFeatures: AccessibleFeature[];
  accessibleRoles: string[];
  enabledFeatureCodes: string[];
}

export interface UserOverride {
  featureCode: string;
  isEnabled: boolean;
  overriddenBy?: string;
}

export interface FeatureData {
  id: string;
  feature_id?: number | null;
  feature_code: string;
  feature_name: string;
  feature_icon?: string;
  sub_features?: SubFeatureData[];
}

export interface SubFeatureData {
  id: string;
  code: string;
  name: string;
  icon?: string;
  is_enabled?: number;
}
